/**
 * ダッシュボード・レポート・レポート雛形の型と入力検証（02 5.3.11 report・D-047〜D-050）。
 * - 期間の指定は JST の日付（from・to の両端を含む）。最大幅 366 日を超えたら 422 LIST_001（D-047。service で確かめる）
 * - 金額は Decimal 文字列（02 2.11）。率は小数 2 桁の Decimal 文字列（画面は小数 1 桁の % で出す：03 3.9）
 * - 雛形の definition_map（ReportDefinition）はデータソースごとの許可列だけ（D-050）
 */
import { z } from 'zod';
import { ACTIVITY_DIRECTION_TYPE_VALUES } from '../enums/activity-direction-type';
import { ACTIVITY_TYPE_VALUES } from '../enums/activity-type';
import { PAYMENT_STATUS_VALUES } from '../enums/payment-status';
import { REPORT_CATEGORY_TYPE_VALUES } from '../enums/report-category-type';
import { SALES_PHASE_TYPE_VALUES } from '../enums/sales-phase-type';
import { RELATIVE_PERIOD_TYPES, compareDate, isValidJstDate } from '../date/jst';
import { zCode, zDate, zEnum, zRequiredText, zSystemCode, zVersion } from './common.schema';
import { CompanySummarySchema, SoleProprietorSummarySchema } from './customer.schema';
import { GoalSchema } from './goal.schema';
import { buildListQuerySchema, CommonAttributesSchema, DEFAULT_PER_PAGE, MAX_PER_PAGE } from './list-query.schema';

/** 期間の最大幅（D-047。from と to の日数差がこれ以下） */
export const REPORT_PERIOD_MAX_DAYS = 366;

// ───────── API-REPT-01 ダッシュボード ─────────

export const DashboardSchema = z.object({
  company_count: z.number().int(),
  sole_proprietor_count: z.number().int(),
  recent_companies: z.array(CompanySummarySchema),
  recent_sole_proprietors: z.array(SoleProprietorSummarySchema),
  active_goals: z.array(GoalSchema),
  open_case_count: z.number().int(),
  this_month_transaction_amount: z.string(),
});

// ───────── API-REPT-02〜07 のクエリ ─────────

export const ReportPeriodQuerySchema = z.object({ from: zDate, to: zDate });

export const SalesForecastQuerySchema = ReportPeriodQuerySchema.extend({ sales_user_code: zSystemCode.optional() });

export const PipelineQuerySchema = z.object({
  sales_user_code: zSystemCode.optional(),
  due_date_from: zDate.optional(),
  due_date_to: zDate.optional(),
});

export const ActivityKpiQuerySchema = SalesForecastQuerySchema;

export const PeriodComparisonQuerySchema = z.object({
  current_from: zDate,
  current_to: zDate,
  previous_from: zDate.optional(),
  previous_to: zDate.optional(),
  sales_user_code: zSystemCode.optional(),
});

export const DailyReportQuerySchema = z.object({ date: zDate.optional(), sales_user_code: zSystemCode.optional() });

export const UserPerformanceQuerySchema = ReportPeriodQuerySchema;

// ───────── API-REPT-02〜07 の応答 ─────────

const FORECAST_VALUES_SHAPE = {
  open_case_count: z.number().int(),
  open_amount: z.string(),
  weighted_amount: z.string(),
  actual_transaction_amount: z.string(),
};

export const SalesForecastSchema = z.object({
  rows: z.array(z.object({ year_month: z.string(), ...FORECAST_VALUES_SHAPE })),
  totals: z.object(FORECAST_VALUES_SHAPE),
});

export const PipelineSchema = z.object({
  rows: z.array(
    z.object({
      case_status: z.string(),
      case_status_name: z.string(),
      display_order: z.number().int(),
      case_count: z.number().int(),
      amount: z.string(),
      weighted_amount: z.string(),
    }),
  ),
  totals: z.object({ case_count: z.number().int(), amount: z.string(), weighted_amount: z.string() }),
});

const countsOf = <const T extends readonly [string, ...string[]]>(values: T) =>
  z.object(Object.fromEntries(values.map((value) => [value, z.number().int()])) as Record<T[number], z.ZodNumber>);

export const ActivityTypeCountsSchema = countsOf(ACTIVITY_TYPE_VALUES);

export const ActivityKpiSchema = z.object({
  total_count: z.number().int(),
  by_activity_type: ActivityTypeCountsSchema,
  by_direction_type: countsOf([...ACTIVITY_DIRECTION_TYPE_VALUES, 'unspecified'] as const),
  by_sales_phase_type: countsOf([...SALES_PHASE_TYPE_VALUES, 'unspecified'] as const),
  by_user: z.array(
    z.object({
      sales_user_code: z.string(),
      sales_user_name: z.string().nullable(),
      total_count: z.number().int(),
      by_activity_type: ActivityTypeCountsSchema,
    }),
  ),
});

export const PERIOD_METRIC_KEYS = [
  'transaction_amount',
  'new_case_count',
  'won_case_count',
  'lost_case_count',
  'win_rate',
  'activity_count',
] as const;

/** 期間比較の指標（件数は数値、金額・率は Decimal 文字列） */
export const PeriodMetricsSchema = z.object({
  transaction_amount: z.string(),
  new_case_count: z.number().int(),
  won_case_count: z.number().int(),
  lost_case_count: z.number().int(),
  win_rate: z.string(),
  activity_count: z.number().int(),
});

export const PeriodComparisonSchema = z.object({
  current_period: z.object({ from: z.string(), to: z.string() }),
  previous_period: z.object({ from: z.string(), to: z.string() }),
  current: PeriodMetricsSchema,
  previous: PeriodMetricsSchema,
  difference: PeriodMetricsSchema,
  /** 当期 ÷ 比較期 × 100（比較期が 0 なら null） */
  ratio: z.object(
    Object.fromEntries(PERIOD_METRIC_KEYS.map((key) => [key, z.string().nullable()])) as Record<
      (typeof PERIOD_METRIC_KEYS)[number],
      z.ZodNullable<z.ZodString>
    >,
  ),
});

export const DailyReportSchema = z.object({
  date: z.string(),
  activities: z.array(
    z.object({
      activity_code: z.string(),
      activity_at: z.string(),
      activity_type: z.enum(ACTIVITY_TYPE_VALUES),
      subject: z.string(),
      activity_content: z.string().nullable(),
      case_code: z.string().nullable(),
      case_name: z.string().nullable(),
      customer_name: z.string().nullable(),
      sales_user_code: z.string(),
      sales_user_name: z.string().nullable(),
    }),
  ),
  cases: z.array(
    z.object({
      case_code: z.string(),
      case_name: z.string(),
      customer_name: z.string().nullable(),
      amount: z.string().nullable(),
      case_status: z.string(),
      case_status_name: z.string().nullable(),
      sales_user_code: z.string(),
      sales_user_name: z.string().nullable(),
      updated_at: z.string(),
    }),
  ),
});

export const UserPerformanceRowSchema = z.object({
  rank: z.number().int(),
  sales_user_code: z.string(),
  sales_user_name: z.string().nullable(),
  case_count: z.number().int(),
  won_count: z.number().int(),
  lost_count: z.number().int(),
  win_rate: z.string(),
  sales_amount: z.string(),
  activity_count: z.number().int(),
  /** 平均商談期間（日・小数 1 桁。受注が無ければ null：D-106） */
  average_deal_days: z.string().nullable(),
});

// ───────── レポート雛形（ReportDefinition：D-050） ─────────

/** 274：旧PCRM のデータソース「企業」「個人事業主」を戻した（companies・soleProprietors。顧客の表から読む） */
export const REPORT_DATA_SOURCE_VALUES = [
  'cases',
  'transactions',
  'activities',
  'companies',
  'soleProprietors',
] as const;
export type ReportDataSource = (typeof REPORT_DATA_SOURCE_VALUES)[number];

export const REPORT_DATA_SOURCE_LABELS: Readonly<Record<ReportDataSource, string>> = {
  cases: '案件',
  transactions: '取引',
  activities: '活動',
  companies: '企業',
  soleProprietors: '個人事業主',
};

/** 列の値の形（画面の表示の仕方） */
export type ReportColumnValueType = 'text' | 'money' | 'number' | 'date' | 'datetime' | 'enum';

export interface ReportColumnDefinition {
  field: string;
  label: string;
  value_type: ReportColumnValueType;
  /** 期間の date_field に選べる列 */
  is_period_field?: boolean;
}

/** データソースごとの許可列（02 5.3.11 の許可列。並びもこの順） */
export const REPORT_COLUMN_DEFINITIONS: Readonly<Record<ReportDataSource, readonly ReportColumnDefinition[]>> = {
  cases: [
    { field: 'case_code', label: '案件コード', value_type: 'text' },
    { field: 'case_name', label: '案件名', value_type: 'text' },
    { field: 'customer_name', label: '顧客名', value_type: 'text' },
    { field: 'case_status_name', label: 'ステータス', value_type: 'text' },
    { field: 'amount', label: '金額', value_type: 'money' },
    { field: 'probability', label: '確度(%)', value_type: 'number' },
    { field: 'weighted_amount', label: '加重金額', value_type: 'money' },
    { field: 'due_date', label: '期日', value_type: 'date', is_period_field: true },
    { field: 'first_contact_date', label: '初回接触日', value_type: 'date', is_period_field: true },
    { field: 'sales_user_name', label: '担当者', value_type: 'text' },
    { field: 'created_at', label: '登録日時', value_type: 'datetime', is_period_field: true },
  ],
  transactions: [
    { field: 'transaction_code', label: '取引コード', value_type: 'text' },
    { field: 'transaction_name', label: '取引名', value_type: 'text' },
    { field: 'customer_name', label: '顧客名', value_type: 'text' },
    { field: 'transaction_status_name', label: 'ステータス', value_type: 'text' },
    { field: 'amount', label: '金額', value_type: 'money' },
    { field: 'quantity', label: '数量', value_type: 'money' },
    { field: 'unit_price', label: '単価', value_type: 'money' },
    { field: 'transaction_date', label: '取引日', value_type: 'date', is_period_field: true },
    { field: 'invoice_date', label: '請求日', value_type: 'date', is_period_field: true },
    { field: 'paid_date', label: '入金日', value_type: 'date', is_period_field: true },
    { field: 'payment_status', label: '支払い状況', value_type: 'enum' },
    { field: 'sales_user_name', label: '担当者', value_type: 'text' },
  ],
  activities: [
    { field: 'activity_at', label: '日時', value_type: 'datetime', is_period_field: true },
    { field: 'activity_type', label: '種別', value_type: 'enum' },
    { field: 'activity_direction_type', label: '方向', value_type: 'enum' },
    { field: 'sales_phase_type', label: '営業フェーズ', value_type: 'enum' },
    { field: 'subject', label: '件名', value_type: 'text' },
    { field: 'customer_name', label: '顧客名', value_type: 'text' },
    { field: 'case_name', label: '案件名', value_type: 'text' },
    { field: 'sales_user_name', label: '担当者', value_type: 'text' },
  ],
  // 274：旧の列（企業名・企業コード・住所・担当者・メール・作成日）。旧の「業種」は新CRM に項目が無いので無い。担当者・メールは企業の主担当者
  companies: [
    { field: 'company_name', label: '企業名', value_type: 'text' },
    { field: 'customer_code', label: '企業コード', value_type: 'text' },
    { field: 'address', label: '住所', value_type: 'text' },
    { field: 'primary_customer_contact_name', label: '担当者', value_type: 'text' },
    { field: 'email', label: 'メール', value_type: 'text' },
    { field: 'created_at', label: '作成日', value_type: 'datetime', is_period_field: true },
  ],
  // 274：旧の列（事業主名・コード・電話番号・メール・作成日）
  soleProprietors: [
    { field: 'sole_proprietor_name', label: '事業主名', value_type: 'text' },
    { field: 'customer_code', label: 'コード', value_type: 'text' },
    { field: 'phone_number', label: '電話番号', value_type: 'text' },
    { field: 'email', label: 'メール', value_type: 'text' },
    { field: 'created_at', label: '作成日', value_type: 'datetime', is_period_field: true },
  ],
};

export function reportColumnOf(data_source: ReportDataSource, field: string): ReportColumnDefinition | undefined {
  return REPORT_COLUMN_DEFINITIONS[data_source].find((column) => column.field === field);
}

/** definition_map の JSON の上限（64KB：02 5.3.11） */
export const REPORT_DEFINITION_MAX_BYTES = 64 * 1024;

const zCodeList = z.array(zCode).max(100, 'MSG-V-020');

export const ReportPeriodSchema = z
  .object({
    date_field: zCode,
    relative_type: z.enum(RELATIVE_PERIOD_TYPES, { message: 'MSG-V-020' }).optional(),
    from: zDate.optional(),
    to: zDate.optional(),
  })
  .strict()
  .superRefine((period, context) => {
    if (period.relative_type && (period.from || period.to)) {
      context.addIssue({ code: 'custom', path: ['relative_type'], message: 'MSG-V-020' });
    }
    // 項目の誤りがあっても superRefine は呼ばれるため、日付の形を確かめてから比べる（from='abc' で compareDate が RangeError → 500：job.schema と同じ）
    if (
      period.from &&
      period.to &&
      isValidJstDate(period.from) &&
      isValidJstDate(period.to) &&
      compareDate(period.to, period.from) < 0
    ) {
      context.addIssue({ code: 'custom', path: ['to'], message: 'FE-MSG-V-006' });
    }
  });

export const ReportFiltersSchema = z
  .object({
    case_status: zCodeList.optional(),
    transaction_status: zCodeList.optional(),
    payment_status: z.array(z.enum(PAYMENT_STATUS_VALUES, { message: 'MSG-V-020' })).optional(),
    activity_type: z.array(z.enum(ACTIVITY_TYPE_VALUES, { message: 'MSG-V-020' })).optional(),
    sales_user_code: z.array(zSystemCode).max(100, 'MSG-V-020').optional(),
    customer_code: zCodeList.optional(),
  })
  .strict();

/** データソースに合わない絞込（例 取引の雛形に case_status） */
const FILTERS_BY_SOURCE: Readonly<Record<ReportDataSource, readonly string[]>> = {
  cases: ['case_status', 'sales_user_code', 'customer_code'],
  transactions: ['transaction_status', 'payment_status', 'sales_user_code', 'customer_code'],
  activities: ['activity_type', 'sales_user_code', 'customer_code'],
  // 274：企業・個人事業主は期間（作成日）と担当者（営業担当）で絞る。ステータスは無い（旧と同じ）
  companies: ['sales_user_code'],
  soleProprietors: ['sales_user_code'],
};

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/** ReportDefinition（definition_map の中身：D-050） */
export const ReportDefinitionSchema = z
  .object({
    data_source: z.enum(REPORT_DATA_SOURCE_VALUES, { message: 'MSG-V-024' }),
    columns: z.array(zCode, { message: 'MSG-V-070' }).min(1, 'MSG-V-070').max(30, 'MSG-V-020'),
    period: ReportPeriodSchema.optional(),
    filters: ReportFiltersSchema.optional(),
    sort: z
      .object({ field: zCode, direction: z.enum(['asc', 'desc'], { message: 'MSG-V-020' }) })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((definition, context) => {
    const allowed = REPORT_COLUMN_DEFINITIONS[definition.data_source];
    definition.columns.forEach((field, index) => {
      if (!allowed.some((column) => column.field === field)) {
        context.addIssue({ code: 'custom', path: ['columns', index], message: 'MSG-V-020' });
      }
    });
    if (new Set(definition.columns).size !== definition.columns.length) {
      context.addIssue({ code: 'custom', path: ['columns'], message: 'MSG-V-020' });
    }
    if (definition.period) {
      const period_column = allowed.find((column) => column.field === definition.period?.date_field);
      if (!period_column?.is_period_field) {
        context.addIssue({ code: 'custom', path: ['period', 'date_field'], message: 'MSG-V-020' });
      }
    }
    for (const key of Object.keys(definition.filters ?? {})) {
      if (!FILTERS_BY_SOURCE[definition.data_source].includes(key)) {
        context.addIssue({ code: 'custom', path: ['filters', key], message: 'MSG-V-020' });
      }
    }
    if (definition.sort && !definition.columns.includes(definition.sort.field)) {
      context.addIssue({ code: 'custom', path: ['sort', 'field'], message: 'MSG-V-020' });
    }
    if (byteLength(definition) > REPORT_DEFINITION_MAX_BYTES) {
      context.addIssue({ code: 'custom', path: [], message: 'MSG-V-005' });
    }
  });

/** API-REPT-09 雛形の登録 */
export const ReportTemplateCreateSchema = z
  .object({
    report_template_name: zRequiredText(200, 'MSG-V-025'),
    report_category_type: zEnum(REPORT_CATEGORY_TYPE_VALUES),
    definition_map: ReportDefinitionSchema,
  })
  .strict();

/** API-REPT-11 雛形の更新（すべて任意＋version） */
export const ReportTemplateUpdateSchema = z
  .object({
    report_template_name: zRequiredText(200, 'MSG-V-025').optional(),
    report_category_type: zEnum(REPORT_CATEGORY_TYPE_VALUES).optional(),
    definition_map: ReportDefinitionSchema.optional(),
    version: zVersion,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

/** API-REPT-08 の 1 行（definition_map を除く） */
export const ReportTemplateSummarySchema = z
  .object({
    report_template_code: z.string(),
    report_template_name: z.string(),
    report_category_type: z.enum(REPORT_CATEGORY_TYPE_VALUES),
    created_by_name: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const ReportTemplateSchema = ReportTemplateSummarySchema.extend({ definition_map: ReportDefinitionSchema });

export const REPORT_TEMPLATE_LIST_SORT_VALUES = ['report_template_name', 'created_at'] as const;

export const ReportTemplateListQuerySchema = buildListQuerySchema(
  REPORT_TEMPLATE_LIST_SORT_VALUES,
  'report_template_name',
).extend({ report_category_type: zEnum(REPORT_CATEGORY_TYPE_VALUES).optional() });

/** API-REPT-13 のクエリ（from・to は雛形の period を上書き） */
export const ReportResultQuerySchema = z.object({
  from: zDate.optional(),
  to: zDate.optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(MAX_PER_PAGE).default(DEFAULT_PER_PAGE),
});

export const ReportResultSchema = z.object({
  columns: z.array(z.object({ field: z.string(), label: z.string() })),
  rows: z.array(z.record(z.union([z.string(), z.number(), z.null()]))),
});

export type Dashboard = z.infer<typeof DashboardSchema>;
export type SalesForecastQuery = z.infer<typeof SalesForecastQuerySchema>;
export type PipelineQuery = z.infer<typeof PipelineQuerySchema>;
export type ActivityKpiQuery = z.infer<typeof ActivityKpiQuerySchema>;
export type PeriodComparisonQuery = z.infer<typeof PeriodComparisonQuerySchema>;
export type DailyReportQuery = z.infer<typeof DailyReportQuerySchema>;
export type UserPerformanceQuery = z.infer<typeof UserPerformanceQuerySchema>;
export type SalesForecast = z.infer<typeof SalesForecastSchema>;
export type Pipeline = z.infer<typeof PipelineSchema>;
export type ActivityKpi = z.infer<typeof ActivityKpiSchema>;
export type PeriodMetrics = z.infer<typeof PeriodMetricsSchema>;
export type PeriodComparison = z.infer<typeof PeriodComparisonSchema>;
export type DailyReport = z.infer<typeof DailyReportSchema>;
export type UserPerformanceRow = z.infer<typeof UserPerformanceRowSchema>;
export type ReportDefinition = z.infer<typeof ReportDefinitionSchema>;
export type ReportTemplateCreateInput = z.infer<typeof ReportTemplateCreateSchema>;
export type ReportTemplateUpdateInput = z.infer<typeof ReportTemplateUpdateSchema>;
export type ReportTemplateSummary = z.infer<typeof ReportTemplateSummarySchema>;
export type ReportTemplate = z.infer<typeof ReportTemplateSchema>;
export type ReportTemplateListQuery = z.infer<typeof ReportTemplateListQuerySchema>;
export type ReportResultQuery = z.infer<typeof ReportResultQuerySchema>;
export type ReportResult = z.infer<typeof ReportResultSchema>;
